import React, {
	useState,
	useRef,
	forwardRef,
	useImperativeHandle,
} from "react";
import styled from "styled-components";

const CalendarTable = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
`;

const CalendarDay = styled.div`
	position: relative;
	display: flex;
	flex: 1 1 calc(100% / 7);
	height: 80px;
	box-sizing: border-box;
	justify-content: center;
	align-items: center;
	padding: 6px;
	opacity: ${(props) => (props.isCurrentMonth ? 1 : 0.6)};
	color: var(
		${(props) =>
			props.isToday
				? "--color-on-tertiary-container"
				: "--color-on-background"}
	);
	background-color: var(
		${(props) =>
			props.isSelected ? "--color-surface-variant" : "--color-surface"}
	);
	cursor: pointer;
	transition: 0.3s;

	&:hover {
		background-color: var(--color-surface-container-highest);
	}

	p {
		z-index: 1;
	}

	&::before {
		content: ${(props) => (props.isToday ? "''" : "")};
		position: absolute;
		top: calc(50% - 12px);
		left: calc(50% - 12px);
		width: 24px;
		height: 24px;
		border-radius: 50%;
		background: var(--color-tertiary-container);
	}

	&::after {
		content: ${(props) => (props.hasEvent ? "''" : "")};
		position: absolute;
		top: calc(50% + 18px);
		left: calc(50% - 6px);
		width: 12px;
		height: 12px;
		border-radius: 50%;
		background: ${(props) => props.eventColor};
	}
`;

function isToday(date) {
	const today = new Date();
	return (
		date.getDate() === today.getDate() &&
		date.getMonth() === today.getMonth() &&
		date.getFullYear() === today.getFullYear()
	);
}

const stringToDate = (str) => {
	const pattern = /(\d{2})\.(\d{2})\.(\d{4})/;
	const date = new Date(str.replace(pattern, "$3-$2-$1"));
	date.setHours(0, 0, 0, 0);
	return date;
};

const CalendarDays = forwardRef(
	({ showEvents, currentDay, onChangeSelectedDay }, ref) => {
		const [selectedDays, setSelectedDays] = useState([]);
		const [isMouseDown, setIsMouseDown] = useState(false);
		const [startDay, setStartDay] = useState(null);
		const [endDay, setEndDay] = useState(null);
		const [isSelecting, setIsSelecting] = useState(false);
		const timeoutRef = useRef(null);

		const firstDayOfMonth = new Date(
			currentDay.getFullYear(),
			currentDay.getMonth(),
			1
		);

		const weekdayOfFirstDay =
			(((firstDayOfMonth.getDay() - 1) % 7) + 7) % 7; // Monday is the first day of the week
		let currentDays = [];

		for (let day = 0; day < 42; day++) {
			if (day === 0 && weekdayOfFirstDay === 0) {
				firstDayOfMonth.setDate(firstDayOfMonth.getDate() - 7);
			} else if (day === 0) {
				firstDayOfMonth.setDate(
					firstDayOfMonth.getDate() + (day - weekdayOfFirstDay)
				);
			} else {
				firstDayOfMonth.setDate(firstDayOfMonth.getDate() + 1);
			}

			let calendarDay = {
				index: day,
				currentMonth:
					firstDayOfMonth.getMonth() === currentDay.getMonth(),
				date: new Date(firstDayOfMonth),
				month: firstDayOfMonth.getMonth(),
				number: firstDayOfMonth.getDate(),
				year: firstDayOfMonth.getFullYear(),
				event: false,
				color: "transparent",
				uid: null,
				cid: null,
			};

			currentDays.push(calendarDay);
		}

		// Add events
		for (let i = 0; i < showEvents.length; i++) {
			const event = showEvents[i];
			const startDate = stringToDate(event.start_date);
			const endDate = stringToDate(event.end_date);

			for (let j = 0; j < currentDays.length; j++) {
				const day = currentDays[j];
				if (day.date >= startDate && day.date <= endDate) {
					day.event = true;
					day.color = String(event.color);
					day.uid = String(event.uid);
					day.cid = String(event.doc_id);
				}
			}
		}

		const getDayFromDate = (date) => {
			console.log("getDayFromDate", date);
			return currentDays.find(
				(day) =>
					day.date.getDate() === date.getDate() &&
					day.date.getMonth() === date.getMonth() &&
					day.date.getFullYear() === date.getFullYear()
			);
		};

		const handleMouseDown = (day) => {
			setIsMouseDown(true);
			timeoutRef.current = setTimeout(() => {
				setIsSelecting(true);
				setStartDay(day);
				setSelectedDays([day]);
				onChangeSelectedDay(day);
			}, 200); // 200ms threshold for distinguishing between click and long press
		};

		const handleMouseUp = (day) => {
			clearTimeout(timeoutRef.current);
			setIsMouseDown(false);

			if (!isSelecting) {
				if (!startDay) {
					setStartDay(day);
					setSelectedDays([day]);
					onChangeSelectedDay(day);
				} else if (!endDay) {
					let range;

					if (day.date.getTime() < startDay.date.getTime()) {
						setEndDay(startDay);
						setStartDay(day);
						range = getDaysInRange(day, startDay);
					} else {
						setEndDay(day);
						range = getDaysInRange(startDay, day);
					}

					setSelectedDays(range);
					onChangeSelectedDay(range);
				} else {
					setStartDay(null);
					setEndDay(null);
					setSelectedDays([]);
					onChangeSelectedDay(null);
				}
			}

			setIsSelecting(false);
		};

		const handleMouseEnter = (day) => {
			if (isMouseDown && isSelecting) {
				const range = getDaysInRange(startDay, day);
				setEndDay(day);
				setSelectedDays(range);
				onChangeSelectedDay(range);
			}
		};

		const getDaysInRange = (start, end) => {
			const range = [];
			let index = start.index;
			let currentDate = currentDays[index];

			while (currentDate.date <= end.date) {
				range.push({
					date: currentDate.date,
					currentMonth: currentDate.currentMonth,
					month: currentDate.month,
					number: currentDate.number,
					year: currentDate.year,
					event: currentDate.event,
					color: currentDate.color,
					uid: currentDate.uid,
					cid: currentDate.cid,
				});

				currentDate = currentDays[++index];
			}
			return range;
		};

		useImperativeHandle(ref, () => ({
			getSelection: () => selectedDays,
			clearSelection: () => {
				setStartDay(null);
				setEndDay(null);
				setSelectedDays([]);
			},
			setSelection: (start, end) => {
				setStartDay(start);
				setEndDay(end);
				const range = getDaysInRange(
					getDayFromDate(start) || currentDays[0],
					getDayFromDate(end) || currentDays[currentDays.length - 1]
				);
				setSelectedDays(range);
			},
		}));

		return (
			<CalendarTable>
				{currentDays.map((day) => {
					const isSelected = selectedDays.some(
						(selectedDay) =>
							selectedDay.date.getTime() === day.date.getTime()
					);

					return (
						<CalendarDay
							key={day.date}
							isCurrentMonth={day.currentMonth}
							isSelected={isSelected}
							isToday={isToday(day.date)}
							hasEvent={day.event}
							eventColor={day.color}
							onMouseDown={() => handleMouseDown(day)}
							onMouseEnter={() => handleMouseEnter(day)}
							onMouseUp={() => handleMouseUp(day)}
						>
							<p>{day.number}</p>
						</CalendarDay>
					);
				})}
			</CalendarTable>
		);
	}
);

export default CalendarDays;
